'use client';

import {
  faCircleExclamation,
  faVideo,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'use-intl';

import { Button } from '@/ui';

import type { VideoPlayerErrorOverlayVariant } from './ui.video-player.types';

interface Props {
  variant: VideoPlayerErrorOverlayVariant;
  onRetry: () => void;
}

export function VideoPlayerErrorOverlay({ variant, onRetry }: Props) {
  const t = useTranslations('VideoPlayer');

  const isProcessing = variant === 'processing';

  return (
    <div
      data-test='video-player-error-overlay'
      data-variant={variant}
      role='alert'
      className='absolute inset-0 z-50 flex items-center justify-center bg-gray-800 p-4'
    >
      <div className='flex w-full max-w-92.25 items-start gap-2'>
        <FontAwesomeIcon
          icon={isProcessing ? faVideo : faCircleExclamation}
          aria-hidden='true'
          className='text-2xl text-gray-200'
        />
        <div className='flex min-w-0 flex-1 flex-col items-start gap-2 text-gray-100'>
          <div className='flex flex-col gap-1'>
            <p className='text-base/6 font-semibold'>
              {isProcessing ? t('processingInProgress') : t('loadError.title')}
            </p>
            <p className='text-sm/5'>
              {isProcessing ? t('processingBody') : t('loadError.body')}
            </p>
          </div>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            weight='normal'
            data-test='video-player-error-retry-button'
            onClick={onRetry}
            className='h-auto p-0 text-gray-100 underline underline-offset-2 hover:bg-transparent hover:text-white focus-visible:ring-gray-100 focus-visible:ring-offset-gray-800'
          >
            {t('loadError.retry')}
          </Button>
        </div>
      </div>
    </div>
  );
}
